<template>
  <ul
    class="m-0 flex w-full list-none flex-col items-center justify-center p-0"
  >
    <li
      v-for="link in computedLinks"
      :key="link.path"
      class="flex h-11 w-full items-center justify-center"
    >
      <router-link
        :to="link.path"
        active-class="bg-primary-200"
        data-testid="menu-item"
        :data-test-route="link.path"
        :class="linkClasses(link.path)"
        :tabindex="isRouteAvailable(link.path) ? 0 : -1"
        :aria-disabled="!isRouteAvailable(link.path)"
        :aria-label="link.label"
        @click="$emit('linkClicked')"
      >
        <AppIcon
          :name="link.icon"
          alt=""
          aria-hidden="true"
          :class="iconClasses(link.path)"
          :size="20"
        />
        <span class="whitespace-nowrap" :class="{ hidden: !hovered }">{{
          link.label
        }}</span>
      </router-link>
    </li>
  </ul>
</template>

<script>
import { FeatureRole, ConfigFlag } from './access/feature-roles'
import { checkFeaturePure } from './access/features'

export default {
  props: {
    hovered: {
      type: Boolean,
      default: false,
    },
    showLinks: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['linkClicked'],
  setup() {
    const config = useRuntimeConfig()
    return { publicConfig: config.public }
  },
  data() {
    return {
      // Seeded from the token at creation time; updated via onAuthRefreshSuccess
      // so the menu re-renders immediately after a token refresh (e.g. after a
      // user starts a trial and new FEATURE_* roles are granted).
      keycloakRoles:
        this.$keycloak?.tokenParsed?.realm_access?.roles?.slice() ?? [],
    }
  },
  computed: {
    links() {
      const roles = this.keycloakRoles
      const cfg = this.publicConfig
      const hasFeature = (name, flag = null) =>
        checkFeaturePure(roles, cfg, name, flag)

      return [
        ...(hasFeature(FeatureRole.TextTranslator)
          ? [
              {
                path: '/translate-text',
                icon: 'custom:sidebar/text-translation',
                label: this.$t('menu.text_translator'),
              },
            ]
          : []),
        ...(hasFeature(FeatureRole.DocumentTranslator)
          ? [
              {
                path: '/translate-document',
                icon: 'custom:sidebar/document-translator',
                label: this.$t('menu.new_document'),
              },
              {
                path: '/documents-viewer',
                icon: 'custom:sidebar/documents',
                label: this.$t('menu.documents'),
              },
            ]
          : []),
        ...(hasFeature(FeatureRole.TextPolisher, ConfigFlag.LanguageCorrection)
          ? [
              {
                path: '/language-correction',
                icon: 'custom:sidebar/text-polisher',
                label: this.$t('menu.language_correction'),
              },
            ]
          : []),
        ...(hasFeature(FeatureRole.Glossary, ConfigFlag.Glossaries)
          ? [
              {
                path: '/glossary',
                icon: 'custom:sidebar/glossary',
                label: this.$t('menu.glossary'),
              },
            ]
          : []),
        ...(hasFeature(FeatureRole.TranslationMemory, ConfigFlag.VectorSearch)
          ? [
              {
                path: '/customer-specific-translations',
                icon: 'custom:sidebar/translation-memory',
                label: this.$t('menu.memory'),
              },
            ]
          : []),
        ...(hasFeature(FeatureRole.Dictionary, ConfigFlag.Dictionaries)
          ? [
              {
                path: '/dictionary',
                icon: 'custom:main-page/dictionary',
                label: this.$t('menu.dictionary'),
              },
            ]
          : []),
      ]
    },
    computedLinks() {
      if (this.showLinks.length > 0) {
        return this.links.filter((link) => this.showLinks.includes(link.path))
      }
      return this.links
    },
  },
  created() {
    if (!this.$keycloak) {
      return
    }
    // Chain onto any handler already set by the Keycloak adapter so we don't
    // displace it. Both the bound handler and the previous one are stored as
    // plain instance properties (not in data) to keep them non-reactive.
    this._prevOnRefreshSuccess = this.$keycloak.onAuthRefreshSuccess ?? null
    this._onRefreshBound = () => {
      if (typeof this._prevOnRefreshSuccess === 'function') {
        this._prevOnRefreshSuccess()
      }
      this.keycloakRoles =
        this.$keycloak?.tokenParsed?.realm_access?.roles?.slice() ?? []
    }
    this.$keycloak.onAuthRefreshSuccess = this._onRefreshBound
  },
  beforeUnmount() {
    // Restore the previous handler so no stale closures are left on the
    // global Keycloak instance after this component is destroyed.
    if (
      this.$keycloak &&
      this.$keycloak.onAuthRefreshSuccess === this._onRefreshBound
    ) {
      this.$keycloak.onAuthRefreshSuccess = this._prevOnRefreshSuccess ?? null
    }
  },
  methods: {
    isRouteAvailable(path) {
      return this.$router.options.routes.some((route) => route.path === path)
    },
    linkClasses(path) {
      return [
        'flex w-full items-center gap-4 px-7 py-3 text-primary-600 no-underline transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600 hover:bg-primary-200',
        this.isRouteAvailable(path)
          ? ''
          : 'pointer-events-none text-neutral-800',
      ]
    },
    iconClasses(path) {
      return [
        'h-6 w-6 min-h-6 min-w-6 shrink-0',
        this.isRouteAvailable(path) ? '' : 'grayscale opacity-35',
      ]
    },
  },
}
</script>
